import { format } from 'date-fns'
import { Card, CardContent, CardHeader, CardTitle } from '@/modules/shared/ui/components/card'
import { Badge } from '@/modules/shared/ui/components/badge'
import { Button } from '@/modules/shared/ui/components/button'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/modules/shared/ui/components/alert-dialog'
import { Loader2, ArrowRight, GitBranch } from 'lucide-react'
import { useVacancySuggestions, useWithdrawSuggestion } from '../../hooks/useVacancySuggestions'
import type { VacancySuggestionDto, VacancySuggestionStatus } from '../../dtos/vacancy-suggestion.dto'

const STATUS_STYLES: Record<VacancySuggestionStatus, string> = {
  Compared: 'bg-muted text-foreground border-border',
  Suggested: 'bg-blue-100 text-blue-700 border-blue-200',
  Accepted: 'bg-green-100 text-green-700 border-green-200',
  Rejected: 'bg-red-100 text-red-700 border-red-200',
  Expired: 'bg-orange-100 text-orange-700 border-orange-200',
  Withdrawn: 'bg-muted text-muted-foreground border-border',
}

interface RowProps {
  suggestion: VacancySuggestionDto
  readOnly: boolean
}

function SuggestionRow({ suggestion, readOnly }: RowProps) {
  const withdrawMutation = useWithdrawSuggestion()

  return (
    <tr className="border-b border-border last:border-0 hover:bg-muted/30 transition-colors">
      <td className="py-3 px-4 text-sm font-medium text-foreground">
        {suggestion.candidateName}
      </td>
      <td className="py-3 px-4 text-sm text-muted-foreground">
        <span className="flex items-center gap-1">
          <span className="truncate max-w-[120px]" title={suggestion.sourceVacancyTitle}>
            {suggestion.sourceVacancyTitle}
          </span>
          <ArrowRight className="w-3 h-3 shrink-0" />
          <span className="truncate max-w-[120px]" title={suggestion.targetVacancyTitle}>
            {suggestion.targetVacancyTitle}
          </span>
        </span>
      </td>
      <td className="py-3 px-4">
        <Badge
          variant="outline"
          className={`text-[11px] px-2 py-0.5 font-semibold border ${STATUS_STYLES[suggestion.status] ?? 'bg-muted text-foreground'}`}
        >
          {suggestion.status}
        </Badge>
      </td>
      <td className="py-3 px-4 text-sm text-muted-foreground">
        {suggestion.suggestedByName ?? '—'}
      </td>
      <td className="py-3 px-4 text-sm text-muted-foreground whitespace-nowrap">
        {suggestion.suggestedAt
          ? format(new Date(suggestion.suggestedAt), 'MMM d, yyyy')
          : format(new Date(suggestion.createdAt), 'MMM d, yyyy')}
      </td>
      {!readOnly && (
        <td className="py-3 px-4">
          {suggestion.status === 'Suggested' ? (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  className="text-destructive border-destructive/30 hover:bg-destructive/5"
                  disabled={withdrawMutation.isPending}
                >
                  {withdrawMutation.isPending ? (
                    <Loader2 className="w-3 h-3 animate-spin" />
                  ) : (
                    'Withdraw'
                  )}
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Withdraw suggestion?</AlertDialogTitle>
                  <AlertDialogDescription>
                    This will revoke the candidate&apos;s response link. The suggestion will be
                    marked as Withdrawn and cannot be undone.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction
                    className="bg-destructive hover:bg-destructive/90 text-destructive-foreground"
                    onClick={() => withdrawMutation.mutate(suggestion.id)}
                  >
                    Withdraw
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          ) : (
            <span className="text-xs text-muted-foreground">—</span>
          )}
        </td>
      )}
    </tr>
  )
}

function SuggestionsListSkeleton() {
  return (
    <div className="space-y-2">
      {[1, 2].map((i) => (
        <div key={i} className="h-12 rounded-md bg-muted animate-pulse" />
      ))}
    </div>
  )
}

interface Props {
  candidateId?: string
  sourceApplicationId?: string
  readOnly?: boolean
}

export function SuggestionsList({ candidateId, sourceApplicationId, readOnly = false }: Props) {
  const { data: suggestions, isLoading } = useVacancySuggestions(
    candidateId ? { candidateId } : undefined,
  )

  const filtered: VacancySuggestionDto[] = (suggestions ?? []).filter((s) =>
    sourceApplicationId ? s.sourceApplicationId === sourceApplicationId : true,
  )

  return (
    <Card className="bg-card shadow-md border-border">
      <CardHeader className="border-b border-border">
        <CardTitle className="text-base text-foreground flex items-center gap-2">
          <GitBranch className="w-4 h-4 text-primary" />
          Cross-Vacancy Suggestions
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-4">
        {isLoading ? (
          <SuggestionsListSkeleton />
        ) : filtered.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-6">
            No cross-vacancy suggestions yet.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead>
                <tr className="text-xs font-semibold text-muted-foreground uppercase tracking-wider border-b border-border">
                  <th className="pb-2 px-4">Candidate</th>
                  <th className="pb-2 px-4">Vacancy (from → to)</th>
                  <th className="pb-2 px-4">Status</th>
                  <th className="pb-2 px-4">Suggested by</th>
                  <th className="pb-2 px-4">Date</th>
                  {!readOnly && <th className="pb-2 px-4">Action</th>}
                </tr>
              </thead>
              <tbody>
                {filtered.map((s) => (
                  <SuggestionRow key={s.id} suggestion={s} readOnly={readOnly} />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  )
}
